import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  FaBook,
  FaChevronDown,
  FaChevronUp,
  FaGlobe,
  FaSearch,
  FaSync,
} from "react-icons/fa";
import {
  listMessageTemplateLibrary,
  syncMessageTemplateLibrary,
} from "../../api/templates.api";
import {
  GROUP_META_TOPIC,
  LIBRARY_CATEGORIES,
  LIBRARY_FILTER_GROUPS_BY_CATEGORY,
  LIBRARY_LANGUAGES,
  OPTION_MATCHERS,
  formatLibraryBody,
  mapManagerLibraryItem,
  toCreateTemplateState,
} from "../../data/whatsappManagerLibrary";
import { businessPath } from "../../config/businessMenu";
import { useProjectPhone } from "../../hooks/useProjectPhone";

const defaultSelected = (groups) => {
  const next = {};
  groups.forEach((group) => {
    next[group.id] = [];
  });
  return next;
};

const defaultOpen = (groups) => {
  const next = {};
  groups.forEach((group) => {
    next[group.id] = Boolean(group.defaultOpen);
  });
  return next;
};

const mapLibraryResponse = (res) => {
  const mapped = [];
  (Array.isArray(res?.data) ? res.data : []).forEach((item) => {
    mapped.push(mapManagerLibraryItem(item, "db"));
  });
  return mapped;
};

const matchesGroup = (item, group, selectedValues) => {
  if (!selectedValues.length) return true;
  const blob = [
    item.topic,
    item.usecase,
    item.name,
    item.title,
    item.authFeature,
    item.authUsecase,
    ...(item.buttonTypes || []),
    ...(item.industries || []),
  ]
    .join(" ")
    .toUpperCase();
  return selectedValues.some((value) => {
    if (group.field === "industries") {
      return (item.industries || []).includes(value);
    }
    if (item.topic === value || item.usecase === value) return true;
    if (item[group.field] === value) return true;
    if (GROUP_META_TOPIC[group.id] === value && item.topic === GROUP_META_TOPIC[group.id]) {
      return true;
    }
    const keys = OPTION_MATCHERS[value] || [value];
    return keys.some((key) => blob.includes(key));
  });
};

const formatSyncedAt = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString();
};

const TemplateLibrary = () => {
  const { projectId, wabaId } = useProjectPhone();
  const [category, setCategory] = useState("utility");
  const [search, setSearch] = useState("");
  const [language, setLanguage] = useState("en_US");
  const [selected, setSelected] = useState(() =>
    defaultSelected(LIBRARY_FILTER_GROUPS_BY_CATEGORY.utility),
  );
  const [openGroups, setOpenGroups] = useState(() =>
    defaultOpen(LIBRARY_FILTER_GROUPS_BY_CATEGORY.utility),
  );
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState("");
  const [syncedAt, setSyncedAt] = useState(null);
  const [syncMessage, setSyncMessage] = useState("");
  const syncingRef = useRef(false);

  const filterGroups = LIBRARY_FILTER_GROUPS_BY_CATEGORY[category] || [];

  const applyResponse = useCallback((res) => {
    const mapped = mapLibraryResponse(res);
    setItems(mapped);
    setSyncedAt(res?.syncedAt || null);
    return mapped;
  }, []);

  const loadLibrary = useCallback(async () => {
    if (!projectId) {
      setItems([]);
      return [];
    }
    setLoading(true);
    setError("");
    try {
      const res = await listMessageTemplateLibrary({
        projectId,
        wabaId,
        language,
      });
      return applyResponse(res);
    } catch (err) {
      setItems([]);
      setError(err.message || "Could not load templates from the local database.");
      return null;
    } finally {
      setLoading(false);
    }
  }, [applyResponse, language, projectId, wabaId]);

  const syncLibrary = useCallback(async () => {
    if (!projectId || syncingRef.current) return [];
    syncingRef.current = true;
    setSyncing(true);
    setError("");
    setSyncMessage("");
    try {
      const res = await syncMessageTemplateLibrary({
        projectId,
        wabaId,
        language,
      });
      const mapped = applyResponse(res);
      setSyncMessage(
        `Synced ${res?.synced ?? mapped.length} templates from WhatsApp.`,
      );
      return mapped;
    } catch (err) {
      setError(
        err.message ||
          "Could not download templates from WhatsApp. Local templates were not changed.",
      );
      return [];
    } finally {
      syncingRef.current = false;
      setSyncing(false);
    }
  }, [applyResponse, language, projectId, wabaId]);

  useEffect(() => {
    loadLibrary();
  }, [loadLibrary]);

  const onCategoryChange = (nextCategory) => {
    const groups = LIBRARY_FILTER_GROUPS_BY_CATEGORY[nextCategory] || [];
    setCategory(nextCategory);
    setSelected(defaultSelected(groups));
    setOpenGroups(defaultOpen(groups));
  };

  const categorizedItems = useMemo(
    () =>
      items.filter((item) => {
        const itemCategory =
          item.category === "authentication" ? "authentication" : "utility";
        return itemCategory === category;
      }),
    [items, category],
  );

  const categoryCounts = useMemo(() => {
    const counts = { utility: 0, authentication: 0 };
    items.forEach((item) => {
      if (counts[item.category] != null) counts[item.category] += 1;
    });
    return counts;
  }, [items]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return categorizedItems.filter((item) => {
      const haystack = `${item.title} ${item.body} ${item.name}`.toLowerCase();
      if (q && !haystack.includes(q)) return false;
      return filterGroups.every((group) =>
        matchesGroup(item, group, selected[group.id] || []),
      );
    });
  }, [categorizedItems, filterGroups, search, selected]);

  const optionCount = (group, optionValue) => {
    const otherSelected = { ...selected, [group.id]: [optionValue] };
    return categorizedItems.filter((item) =>
      filterGroups.every((g) => matchesGroup(item, g, otherSelected[g.id] || [])),
    ).length;
  };

  const toggleOption = (groupId, value) => {
    setSelected((prev) => {
      const current = prev[groupId] || [];
      const next = current.includes(value)
        ? current.filter((item) => item !== value)
        : [...current, value];
      return { ...prev, [groupId]: next };
    });
  };

  const busy = loading || syncing;
  const syncedLabel = formatSyncedAt(syncedAt);

  return (
    <div className="biz-panel-page biz-templatelib-page wam-templatelib-page">
      <div className="biz-templatelib-hero">
        <h1>
          <FaBook />
          Template library
        </h1>
        <p>
          Browse templates stored locally. Use Sync to download the latest
          catalog from WhatsApp Manager.
        </p>
      </div>

      <div className="wam-templatelib-toolbar">
        <label className="wam-templatelib-search">
          <FaSearch />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={
              category === "authentication"
                ? "Search Authentication templates"
                : "Search Utility templates"
            }
          />
        </label>
        <label className="wam-templatelib-language">
          <FaGlobe />
          <select value={language} onChange={(e) => setLanguage(e.target.value)}>
            {LIBRARY_LANGUAGES.map((item) => (
              <option key={item.value} value={item.value}>
                {item.label} templates
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          className="biz-btn-primary wam-templatelib-sync"
          onClick={syncLibrary}
          disabled={!projectId || busy}
        >
          <FaSync className={syncing ? "is-spinning" : ""} />
          {syncing ? "Syncing…" : "Sync"}
        </button>
      </div>

      {error && <p className="create-template-error wam-templatelib-error">{error}</p>}
      {syncMessage && !error && (
        <p className="wam-templatelib-sync-status">{syncMessage}</p>
      )}

      <div className="biz-templatelib-layout wam-templatelib-layout">
        <aside className="wam-templatelib-filters">
          <section className="wam-filter-group wam-filter-category">
            <h2 className="wam-filter-heading">Category</h2>
            <div className="wam-filter-options">
              {LIBRARY_CATEGORIES.map((item) => (
                <label key={item.value} className="wam-filter-option">
                  <input
                    type="radio"
                    name="library-category"
                    checked={category === item.value}
                    onChange={() => onCategoryChange(item.value)}
                  />
                  <span>
                    {item.label}
                    <em>({categoryCounts[item.value] || 0})</em>
                  </span>
                </label>
              ))}
            </div>
          </section>

          {filterGroups.map((group) => {
            const opened = openGroups[group.id];
            return (
              <section key={group.id} className="wam-filter-group">
                <button
                  type="button"
                  className="wam-filter-heading"
                  onClick={() =>
                    setOpenGroups((prev) => ({ ...prev, [group.id]: !prev[group.id] }))
                  }
                >
                  <span>{group.label}</span>
                  {opened ? <FaChevronUp /> : <FaChevronDown />}
                </button>
                {opened && (
                  <div className="wam-filter-options">
                    {group.options.map((option) => {
                      const count = optionCount(group, option.value);
                      const checked = (selected[group.id] || []).includes(option.value);
                      return (
                        <label key={option.value} className="wam-filter-option">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleOption(group.id, option.value)}
                          />
                          <span>
                            {option.label}
                            <em>({count})</em>
                          </span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </section>
            );
          })}
        </aside>

        <div className="wam-templatelib-results">
          <p className="wam-templatelib-count">
            {syncing
              ? "Downloading templates from WhatsApp…"
              : loading
                ? "Loading templates…"
                : `Showing ${filtered.length} of ${Math.max(
                    categorizedItems.length,
                    filtered.length,
                  )} results`}
            {syncedLabel ? ` · last synced ${syncedLabel}` : " · local database"}
          </p>
          <div className="wam-templatelib-grid">
            {filtered.map((item) => (
              <article key={`${item.language}-${item.name}`} className="wam-template-card">
                <div className="wam-template-preview">
                  <div className="wam-wa-bubble">
                    <h3>{item.title}</h3>
                    <p>{formatLibraryBody(item.body)}</p>
                    <time>9:42 AM</time>
                    {(item.buttons || []).slice(0, 2).map((button) => (
                      <span key={button.title} className="wam-wa-button">
                        {button.title}
                      </span>
                    ))}
                  </div>
                </div>
                <code>{item.name}</code>
                <Link
                  to={businessPath(projectId, "createtemplate")}
                  className="biz-btn-primary"
                  state={{ template: toCreateTemplateState(item) }}
                >
                  Use template
                </Link>
              </article>
            ))}
            {!busy && filtered.length === 0 && (
              <p className="biz-table-empty">
                {items.length
                  ? "No templates match these filters."
                  : "No templates in the local database yet. Click Sync to download them from WhatsApp."}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TemplateLibrary;
